import React, { useEffect, useMemo, useState } from 'react'
import { Link } from '@inertiajs/react'
import { Box, Typography, Chip, Paper } from '@mui/material'
import {
  APIProvider,
  Map,
  AdvancedMarker,
  InfoWindow,
  Pin,
  useMap,
} from '@vis.gl/react-google-maps'
import naviPlaceholder from '../images/navi/navi_placeholder.png'

export interface MappableLocation {
  id: number
  slug?: string | null
  name: string
  address: string | null
  category: string | null
  latitude: number | null
  longitude: number | null
  google_place_id?: string | null
}

interface Props {
  apiKey: string
  locations: MappableLocation[]
  height?: number | string
}

const SEOUL_CENTER = { lat: 37.5665, lng: 126.9780 }
const MAP_ID = 'okonomi-kpop-locations-map'

const categoryLabels: Record<string, string> = {
  restaurant: 'Restaurant',
  cafe: 'Cafe',
  filming_location: 'Filming Location',
  practice_studio: 'Practice Studio',
  entertainment_venue: 'Entertainment Venue',
  shop: 'Shop',
  landmark: 'Landmark',
  other: 'Other',
}

function FitBounds({ points }: { points: { lat: number; lng: number }[] }) {
  const map = useMap()

  useEffect(() => {
    if (!map || points.length === 0) return

    if (points.length === 1) {
      map.setCenter(points[0])
      map.setZoom(14)
      return
    }

    const bounds = new google.maps.LatLngBounds()
    points.forEach((p) => bounds.extend(p))
    map.fitBounds(bounds, 64)
  }, [map, points])

  return null
}

export default function LocationsMap({ apiKey, locations, height = 400 }: Props) {
  const [openId, setOpenId] = useState<number | null>(null)
  const [photosByPlaceId, setPhotosByPlaceId] = useState<Record<string, string[] | 'loading' | 'error'>>({})

  const mappable = useMemo(
    () =>
      locations.filter(
        (l): l is MappableLocation & { latitude: number; longitude: number } =>
          l.latitude !== null && l.longitude !== null,
      ),
    [locations],
  )

  const points = useMemo(
    () => mappable.map((l) => ({ lat: l.latitude, lng: l.longitude })),
    [mappable],
  )

  const opened = mappable.find((l) => l.id === openId) || null

  useEffect(() => {
    const placeId = opened?.google_place_id
    if (!placeId) return
    if (photosByPlaceId[placeId] !== undefined) return

    setPhotosByPlaceId((prev) => ({ ...prev, [placeId]: 'loading' }))
    fetch(`/api/v1/google_maps/photos/${encodeURIComponent(placeId)}?max_width=400&limit=5`, {
      headers: { Accept: 'application/json' },
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(res.statusText)))
      .then((data) => {
        setPhotosByPlaceId((prev) => ({ ...prev, [placeId]: data.photos || [] }))
      })
      .catch(() => {
        setPhotosByPlaceId((prev) => ({ ...prev, [placeId]: 'error' }))
      })
  }, [opened, photosByPlaceId])

  const openedPhotos = opened?.google_place_id ? photosByPlaceId[opened.google_place_id] : undefined

  if (!apiKey) return null
  if (mappable.length === 0) return null

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden', mb: 4 }} data-testid="locations-map">
      <style>{`
        .gm-style-iw.gm-style-iw-c { padding: 0 !important; border-radius: 14px !important; }
        .gm-style-iw-d { overflow: hidden !important; padding: 0 !important; }
        .gm-style-iw-d > * { box-sizing: border-box; }
        .gm-style-iw-chr { position: absolute !important; top: 4px !important; right: 4px !important; height: auto !important; min-height: 0 !important; padding: 0 !important; background: transparent !important; }
        .gm-style-iw-chr .gm-ui-hover-effect { width: 26px !important; height: 26px !important; opacity: 0.85; }
        .gm-style-iw-chr .gm-ui-hover-effect > span { margin: 6px !important; width: 14px !important; height: 14px !important; }
        .gm-style-iw-tc::after { background: #ffffff !important; }
      `}</style>
      <Box sx={{ height, width: '100%' }}>
        <APIProvider apiKey={apiKey}>
          <Map
            mapId={MAP_ID}
            defaultCenter={SEOUL_CENTER}
            defaultZoom={11}
            gestureHandling="greedy"
            disableDefaultUI={false}
            style={{ width: '100%', height: '100%' }}
          >
            <FitBounds points={points} />
            {mappable.map((loc) => (
              <AdvancedMarker
                key={loc.id}
                position={{ lat: loc.latitude, lng: loc.longitude }}
                onClick={() => setOpenId(loc.id)}
                title={loc.name}
              >
                <Pin background="#7B5BD6" glyphColor="#ffffff" borderColor="#6A47C9" />
              </AdvancedMarker>
            ))}
            {opened && (
              <InfoWindow
                position={{ lat: opened.latitude, lng: opened.longitude }}
                onCloseClick={() => setOpenId(null)}
                pixelOffset={[0, -36]}
              >
                <Box sx={{ width: 240 }}>
                  {Array.isArray(openedPhotos) && openedPhotos.length > 0 ? (
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 0.5,
                        overflowX: 'auto',
                      }}
                      data-testid="info-window-photos"
                    >
                      {openedPhotos.map((url, idx) => (
                        <Box
                          key={idx}
                          component="img"
                          src={url}
                          alt={`${opened.name} photo ${idx + 1}`}
                          loading="lazy"
                          sx={{
                            width: 120,
                            height: 90,
                            objectFit: 'cover',
                            flexShrink: 0,
                          }}
                        />
                      ))}
                    </Box>
                  ) : openedPhotos === 'loading' ? (
                    <Box
                      sx={{
                        width: '100%',
                        height: 90,
                        backgroundColor: 'rgba(123, 91, 214,0.08)',
                      }}
                    />
                  ) : (
                    <Box
                      component="img"
                      src={naviPlaceholder}
                      alt={`${opened.name} placeholder`}
                      loading="lazy"
                      data-testid="info-window-placeholder"
                      sx={{
                        width: '100%',
                        height: 110,
                        objectFit: 'contain',
                        backgroundColor: 'rgba(123, 91, 214,0.08)',
                        display: 'block',
                      }}
                    />
                  )}
                  <Box sx={{ px: 1.25, pt: 1, pb: 1.25, pr: 4 }}>
                    <Typography
                      variant="body2"
                      component={Link}
                      href={`/locations/${opened.slug || opened.id}`}
                      sx={{
                        fontWeight: 600,
                        color: 'primary.main',
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' },
                        display: 'block',
                        mb: 0.5,
                        lineHeight: 1.3,
                      }}
                    >
                      {opened.name}
                    </Typography>
                    {opened.category && (
                      <Chip
                        label={categoryLabels[opened.category] || opened.category}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ mb: 0.5, height: 22 }}
                      />
                    )}
                    {opened.address && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.35 }}>
                        {opened.address}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </InfoWindow>
            )}
          </Map>
        </APIProvider>
      </Box>
    </Paper>
  )
}
